import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {Box, Text, VStack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {containerStyle, messageStyle} from './index.style'

const parseApproachingDiscounts = (value) => {
    if (Array.isArray(value)) {
        return value
    }
    if (typeof value === 'string' && value.length > 0) {
        try {
            const parsed = JSON.parse(value)
            return Array.isArray(parsed) ? parsed : []
        } catch (e) {
            return []
        }
    }
    return []
}

const resolveCurrency = (...candidates) => {
    for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
            return candidate.trim()
        }
    }
    return 'USD'
}

const ApproachingPromotionMessage = ({approachingDiscounts, currency: currencyProp}) => {
    const {formatNumber} = useIntl()
    const {currency: activeCurrency} = useCurrency()
    const discounts = parseApproachingDiscounts(approachingDiscounts)

    if (discounts.length === 0) {
        return null
    }

    const formatMoney = (amount, currency) => {
        if (!Number.isFinite(amount) || !currency) {
            return ''
        }
        return formatNumber(amount, {style: 'currency', currency})
    }

    return (
        <VStack
            sx={containerStyle}
            spacing={2}
            align="stretch"
            data-testid="approaching-promotion-message"
            role="status"
            aria-live="polite"
        >
            {discounts.map((promo, index) => {
                const key = promo.promotionId || `approaching-${index}`
                const currency = resolveCurrency(promo.currency, currencyProp, activeCurrency)
                const distance = Number(promo.distance)
                const discountValue = Number(promo.discountValue)
                const formattedDistance = formatMoney(distance, currency)

                if (promo.discountType === 'percentage' && Number.isFinite(discountValue)) {
                    return (
                        <Text key={key} sx={messageStyle}>
                            <FormattedMessage
                                id="approaching_promotion.message.percentage_off"
                                defaultMessage="Spend {distance} more to get {discountValue}% off"
                                values={{
                                    distance: formattedDistance,
                                    discountValue
                                }}
                            />
                        </Text>
                    )
                }

                if (promo.discountType === 'amount' && Number.isFinite(discountValue)) {
                    return (
                        <Text key={key} sx={messageStyle}>
                            <FormattedMessage
                                id="approaching_promotion.message.amount_off"
                                defaultMessage="Spend {distance} more to get {discountValue} off"
                                values={{
                                    distance: formattedDistance,
                                    discountValue: formatMoney(discountValue, currency)
                                }}
                            />
                        </Text>
                    )
                }

                if (promo.calloutMsg) {
                    return (
                        <Text key={key} sx={messageStyle}>
                            {formattedDistance ? (
                                <FormattedMessage
                                    id="approaching_promotion.message.callout"
                                    defaultMessage="Spend {distance} more to get {calloutMsg}"
                                    values={{
                                        distance: formattedDistance,
                                        calloutMsg: (
                                            <Box
                                                as="span"
                                                dangerouslySetInnerHTML={{__html: promo.calloutMsg}}
                                            />
                                        )
                                    }}
                                />
                            ) : (
                                <Box
                                    as="span"
                                    dangerouslySetInnerHTML={{__html: promo.calloutMsg}}
                                />
                            )}
                        </Text>
                    )
                }

                return null
            })}
        </VStack>
    )
}

ApproachingPromotionMessage.propTypes = {
    approachingDiscounts: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
    currency: PropTypes.string
}

export default ApproachingPromotionMessage
